import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Chip, Grid, Stack, Typography } from '@mui/material';

import { useAppSelector } from '../../../../hooks';
import { getAuthState } from '../../../../store/AuthSlice';
import { adminPublishQuiz, IQuiz } from '../../../../store/QuizSlice';
import {
  checkUserPermission,
  getQuestionDifficultyChipColor,
} from '../../../../helper';
import CustomGrayContainer from '../../../../components/Containers/CustomGrayContainer';
import { getTenantsState } from '../../../../store/V2/TenantsSlice';

export interface IQuizDetailHeader
  extends React.HTMLAttributes<HTMLDivElement> {
  quiz: IQuiz;
}

const QuizDetailHeader: React.FC<IQuizDetailHeader> = ({ quiz, ...props }) => {
  const dispatch = useDispatch();
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);

  // State
  const [linkCopied, setLinkCopied] = useState<boolean>(false);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  const updateQuizPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminQuiz: ['publish'],
    },
  });

  // METHODS
  const handleQuizPublish = () => {
    if (updateQuizPermission && quiz) {
      dispatch(
        adminPublishQuiz({
          id: quiz._id,
          isPublished: !quiz.isPublished,
        })
      );
    }
  };

  const handleCopyLink = () => {
    const link = `${process.env.REACT_APP_TUTOPIYA_ASSESSMENT_URL}/startSubmission/${quiz._id}`;

    if ('clipboard' in navigator) {
      navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
      }, 5000);
    }
  };

  // RENDER
  return (
    <CustomGrayContainer {...props}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          pt: '7px',
          pb: '7px',
          pl: { md: '30px', lg: '30px' },
          pr: { md: '30px', lg: '30px' },
        }}
      >
        <div>
          <Grid container spacing={2}>
            <Grid item>
              <Stack direction="row" alignItems="center">
                <Typography color="secondary" variant="subtitle2">
                  Overall Difficulty
                </Typography>
                <Chip
                  size="small"
                  label={quiz.difficulty.toLocaleLowerCase()}
                  color={getQuestionDifficultyChipColor(quiz.difficulty)}
                  style={{
                    borderRadius: '6px',
                    textTransform: 'capitalize',
                    marginLeft: '5px',
                  }}
                />
              </Stack>
            </Grid>
            <Grid item>
              <Stack direction="row" alignItems="center">
                <Typography color="secondary" variant="subtitle2">
                  Number Of Questions
                </Typography>
                <Chip
                  size="small"
                  label={`${quiz.noOfQuestions} Question${
                    quiz.noOfQuestions > 1 ? 's' : ''
                  }`}
                  style={{
                    borderRadius: '6px',
                    textTransform: 'capitalize',
                    marginLeft: '5px',
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </div>

        <div>
          {quiz.assessment && quiz.isPublished && (
            <Button
              variant="contained"
              onClick={handleCopyLink}
              style={{ marginRight: '10px' }}
            >
              {linkCopied ? 'Copied' : 'Copy Link'}
            </Button>
          )}

          {updateQuizPermission && (
            <Button
              variant="contained"
              color={quiz.isPublished ? 'secondary' : 'info'}
              onClick={handleQuizPublish}
            >
              {quiz.isPublished ? 'Unpublish' : 'Publish'}
            </Button>
          )}
        </div>
      </Stack>
    </CustomGrayContainer>
  );
};

export default QuizDetailHeader;
